import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import ClientList from "./pages/ClientList";
import ContactsPage from "./pages/ContactsPage";
import BriefingPage from "./pages/BriefingPage";
import AgendaPage from "./pages/AgendaPage";
import AssistantPage from "./pages/AssistantPage";
import TodayPage from "./pages/TodayPage";
import ProgramsPage from "./pages/ProgramsPage";
import MatchesPage from "./pages/MatchesPage";
import OnboardingPage from "./pages/OnboardingPage";
import ClausesPage from "./pages/ClausesPage";
import RequestsPage from "./pages/RequestsPage";

export const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;
import AddClient from "./pages/AddClient";
import ClientHomepage from "./pages/ClientHomepage";
import CasesInbox from "./pages/CasesInbox";
import UnifiedInbox from "./pages/UnifiedInbox";
import CaseDetail from "./pages/CaseDetail";
import ContractsGlobal from "./pages/ContractsGlobal";
import ScheduleGlobal from "./pages/ScheduleGlobal";
import CommissionsGlobal from "./pages/CommissionsGlobal";
import NotesGlobal from "./pages/NotesGlobal";
import Legal from "./pages/Legal";
import ScoutingPdf from "./pages/ScoutingPdf";
import Compliance from "./pages/Compliance";
import PositionMap from "./pages/PositionMap";
import TravelPlanner from "./pages/TravelPlanner";
import TechDirectorHome from "./pages/TechDirectorHome";
import ScoutingDocs from "./pages/ScoutingDocs";
import ScoutingHome from "./pages/ScoutingHome";
import StaffHome from "./pages/StaffHome";
import AuditLog from "./pages/AuditLog";
import ClubStaff from "./pages/ClubStaff";
import SettingsIntegrations from "./pages/SettingsIntegrations";
import SettingsTeam from "./pages/SettingsTeam";
import SettingsProfile from "./pages/SettingsProfile";
import SettingsPermissions from "./pages/SettingsPermissions";
import MandateHub from "./pages/MandateHub";
import InvitationLanding from "./pages/InvitationLanding";
import WhatsAppTemplates from "./pages/WhatsAppTemplates";
import OAuthCallback from "./pages/OAuthCallback";
import Reports from "./pages/Reports";
import SettingsReports from "./pages/SettingsReports";
import NotFound from "./pages/NotFound";
import PortalShell from "./pages/portal/PortalShell";
import PortalInvite from "./pages/portal/PortalInvite";
import LegalPage from "./pages/LegalPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminWhatsAppTest from "./pages/AdminWhatsAppTest";
import SettingsNotifications from "./pages/SettingsNotifications";
import AdminFeedback from "./pages/AdminFeedback";
import { UpdateToast } from "./pwa/UpdateToast";
import { ForgotPassword, ResetPassword, VerifyEmail } from "./pages/AuthExtras";

function HomeRedirect() {
  const { user, authChecked } = useAuth();
  if (!authChecked) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "player" || user.role === "guardian") return <Navigate to="/portal" replace />;
  if (user.role === "tech_director") return <Navigate to="/td-home" replace />;
  if (user.role === "head_scout" || user.role === "scout") return <Navigate to="/scouting-home" replace />;
  if (["head_coach", "asst_coach", "club_doctor", "physio"].includes(user.role)) return <Navigate to="/staff-home" replace />;
  if ((user.role === "agent" || user.role === "admin") && isMobile()) return <Navigate to="/today" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/cookies" element={<LegalPage slug="cookies" />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/whatsapp" element={<ProtectedRoute><AdminWhatsAppTest /></ProtectedRoute>} />
            <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedback /></ProtectedRoute>} />
            <Route path="/auth/outlook/callback" element={<OAuthCallback provider="outlook" />} />
            <Route path="/auth/gmail/callback" element={<OAuthCallback provider="gmail" />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute><ClientList /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
            <Route path="/briefing" element={<ProtectedRoute><BriefingPage /></ProtectedRoute>} />
            <Route path="/agenda" element={<ProtectedRoute><AgendaPage /></ProtectedRoute>} />
            <Route path="/assistant" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
            <Route path="/today" element={<ProtectedRoute><TodayPage /></ProtectedRoute>} />
            <Route path="/programs" element={<ProtectedRoute><ProgramsPage /></ProtectedRoute>} />
            <Route path="/matches" element={<ProtectedRoute><MatchesPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/clauses" element={<ProtectedRoute><ClausesPage /></ProtectedRoute>} />
            <Route path="/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
            <Route path="/clients/new" element={<ProtectedRoute><AddClient /></ProtectedRoute>} />
            <Route path="/clients/:id" element={<ProtectedRoute><ClientHomepage /></ProtectedRoute>} />
            <Route path="/cases" element={<ProtectedRoute><CasesInbox /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><UnifiedInbox /></ProtectedRoute>} />
            <Route path="/cases/:id" element={<ProtectedRoute><CaseDetail /></ProtectedRoute>} />
            <Route path="/contracts" element={<ProtectedRoute><ContractsGlobal /></ProtectedRoute>} />
            <Route path="/schedule" element={<ProtectedRoute><ScheduleGlobal /></ProtectedRoute>} />
            <Route path="/commissions" element={<ProtectedRoute><CommissionsGlobal /></ProtectedRoute>} />
            <Route path="/notes" element={<ProtectedRoute><NotesGlobal /></ProtectedRoute>} />
            <Route path="/commissions/invoices" element={<ProtectedRoute><CommissionsGlobal /></ProtectedRoute>} />
            <Route path="/payroll" element={<ProtectedRoute><CommissionsGlobal /></ProtectedRoute>} />
            <Route path="/payroll/invoices" element={<ProtectedRoute><CommissionsGlobal /></ProtectedRoute>} />
            <Route path="/legal" element={<ProtectedRoute><Legal /></ProtectedRoute>} />
            <Route path="/clients/:id/scout-pdf" element={<ProtectedRoute><ScoutingPdf /></ProtectedRoute>} />
            <Route path="/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/settings/reports" element={<ProtectedRoute><SettingsReports /></ProtectedRoute>} />
            <Route path="/position-map" element={<ProtectedRoute><PositionMap /></ProtectedRoute>} />
            <Route path="/travel-planner" element={<ProtectedRoute><TravelPlanner /></ProtectedRoute>} />
            <Route path="/td-home" element={<ProtectedRoute><TechDirectorHome /></ProtectedRoute>} />
            <Route path="/scouting-home" element={<ProtectedRoute><ScoutingHome /></ProtectedRoute>} />
            <Route path="/staff-home" element={<ProtectedRoute><StaffHome /></ProtectedRoute>} />
            <Route path="/scouting-docs" element={<ProtectedRoute><ScoutingDocs /></ProtectedRoute>} />
            <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
            <Route path="/club-staff" element={<ProtectedRoute><ClubStaff /></ProtectedRoute>} />
            <Route path="/settings/integrations" element={<ProtectedRoute><SettingsIntegrations /></ProtectedRoute>} />
            <Route path="/settings/team" element={<ProtectedRoute><SettingsTeam /></ProtectedRoute>} />
            <Route path="/settings/permissions" element={<ProtectedRoute><SettingsPermissions /></ProtectedRoute>} />
            <Route path="/settings/profile" element={<ProtectedRoute><SettingsProfile /></ProtectedRoute>} />
            <Route path="/settings/notifications" element={<ProtectedRoute><SettingsNotifications /></ProtectedRoute>} />
            <Route path="/mandates" element={<ProtectedRoute><MandateHub /></ProtectedRoute>} />
            <Route path="/settings/whatsapp-templates" element={<ProtectedRoute><WhatsAppTemplates /></ProtectedRoute>} />
            <Route path="/invite/:token" element={<InvitationLanding />} />
            <Route path="/portal/invite/:token" element={<PortalInvite />} />
            <Route path="/portal/*" element={<PortalShell />} />
            <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
          </Routes>
          <UpdateToast />
        </BrowserRouter>
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
